import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Menu, X, Phone, MapPin, Mail } from "lucide-react";
import { BUSINESS, NAV } from "@/lib/site-data";
import { img } from "@/lib/images";
import { useFocusTrap } from "@/hooks/use-focus-trap";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const menuRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setOpen(false), []);

  useFocusTrap(open, menuRef, closeMenu);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const onDark = !scrolled;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-700 ${
          scrolled ? "glass border-b border-border py-3 shadow-[var(--shadow-soft)]" : "py-6"
        }`}
      >
        <div className="shell flex items-center justify-between gap-4">
          <Link to="/" className="group flex min-w-0 items-center gap-3">
            <img
              src={img("logo")}
              alt=""
              width={128}
              height={128}
              decoding="async"
              fetchPriority="low"
              className="size-14 shrink-0 object-contain sm:size-16"
            />

            <span className="flex min-w-0 flex-col leading-none">
              <span
                className={`display text-2xl transition-colors duration-500 sm:text-[1.65rem] ${
                  onDark ? "text-[oklch(0.97_0.008_240)]" : "text-foreground"
                } group-hover:text-[var(--amber-warm)]`}
              >
                Magnolia House
              </span>
              <span
                className={`mt-1 text-[9px] uppercase tracking-[0.3em] transition-colors duration-500 ${
                  onDark ? "text-[oklch(0.82_0.02_240)]" : "text-muted-foreground"
                }`}
              >
                Custom Picture Framing · Established {BUSINESS.since}
              </span>
            </span>
          </Link>

          <nav aria-label="Primary" className="hidden items-center gap-7 whitespace-nowrap xl:flex">
            {NAV.slice(1, 7).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                preload="intent"
                aria-current={pathname === item.to ? "page" : undefined}
                className={`link-underline text-[11px] uppercase tracking-[0.22em] transition-colors duration-500 ${
                  pathname === item.to
                    ? "text-[var(--amber-warm)]"
                    : onDark
                      ? "text-[oklch(0.88_0.02_240)] hover:text-[oklch(1_0_0)]"
                      : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-3">
            <a
              href={BUSINESS.phoneHref}
              className={`hidden items-center gap-2 rounded-full border px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] transition-all duration-500 hover:-translate-y-0.5 sm:inline-flex ${
                onDark
                  ? "border-white/35 text-[oklch(0.97_0.008_240)] hover:border-[var(--amber-warm)] hover:text-[var(--amber-warm)]"
                  : "border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground"
              }`}
            >
              <Phone className="size-3.5" aria-hidden />
              {BUSINESS.phone}
            </a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              aria-controls="site-menu"
              className={`grid size-11 place-items-center rounded-full border transition-colors duration-500 ${
                onDark
                  ? "border-white/35 text-[oklch(0.97_0.008_240)] hover:border-[var(--amber-warm)]"
                  : "border-border text-foreground hover:border-primary"
              }`}
            >
              <Menu className="size-4" aria-hidden />
            </button>
          </div>
        </div>
      </header>

      {open ? (
        <div
          ref={menuRef}
          id="site-menu"
          role="dialog"
          aria-modal="true"
          aria-labelledby="site-menu-title"
          tabIndex={-1}
          className="night fixed inset-0 z-[70] animate-fade-blur overflow-y-auto"
        >
          <div className="shell flex items-center justify-between py-6">
            <span className="flex items-center gap-3">
              <img
                src={img("logo")}
                alt=""
                width={128}
                height={128}
                className="size-14 object-contain"
              />

              <span id="site-menu-title" className="display text-2xl text-[oklch(0.97_0.008_240)]">
                Magnolia House
              </span>
            </span>
            <button
              type="button"
              onClick={closeMenu}
              aria-label="Close menu"
              className="grid size-11 place-items-center rounded-full border border-white/30 text-[oklch(0.97_0.008_240)] transition-colors hover:border-[var(--amber-warm)] hover:text-[var(--amber-warm)]"
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
          <nav aria-label="All pages" className="shell flex flex-col gap-1 pb-16 pt-6">
            {NAV.map((item, i) => (
              <Link
                key={item.to}
                to={item.to}
                preload="intent"
                onClick={closeMenu}
                aria-current={pathname === item.to ? "page" : undefined}
                style={{ animationDelay: `${i * 45}ms` }}
                className="display animate-rise w-fit text-4xl text-[oklch(0.97_0.008_240)] transition-colors duration-500 hover:text-[var(--amber-warm)] sm:text-6xl"
              >
                {item.label}
              </Link>
            ))}
            <a
              href={BUSINESS.phoneHref}
              className="mt-10 w-fit text-xs uppercase tracking-[0.3em] text-[var(--amber-warm)]"
            >
              Call Shirley · {BUSINESS.phone}
            </a>
          </nav>
        </div>
      ) : null}
    </>
  );
}

export function SiteFooter() {
  return (
    <footer className="night relative overflow-hidden pt-20 pb-10">
      <div className="shell grid gap-12 md:grid-cols-[1.3fr_1fr_1fr]">
        <div>
          <p className="flex items-center gap-3">
            <img
              src={img("logo")}
              alt=""
              width={128}
              height={128}
              loading="lazy"
              decoding="async"
              className="size-16 object-contain"
            />

            <span className="display text-3xl text-[oklch(0.97_0.008_240)]">Magnolia House</span>
          </p>
          <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
            Custom, archival picture framing designed one piece at a time by Certified Professional
            Framer Shirley Hunsberger — in Fuquay-Varina since {BUSINESS.since}.
          </p>
          <a href={BUSINESS.phoneHref} className="btn-ghost mt-7 text-[var(--amber-warm)]">
            <Phone className="size-3.5" aria-hidden />
            Call Shirley
          </a>
        </div>

        <nav aria-label="Footer">
          <h2 className="eyebrow-light">Explore</h2>
          <ul className="mt-5 space-y-2.5">
            {NAV.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="link-underline text-sm text-muted-foreground transition-colors hover:text-[oklch(1_0_0)]"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h2 className="eyebrow-light">Visit</h2>
          <ul className="mt-5 space-y-4 text-sm text-muted-foreground">
            <li className="flex gap-3">
              <MapPin className="mt-0.5 size-4 shrink-0 text-[var(--amber-warm)]" aria-hidden />
              <a
                href={BUSINESS.mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline"
              >
                {BUSINESS.address}
                <span className="sr-only"> (opens in Google Maps)</span>
              </a>
            </li>
            <li className="flex gap-3">
              <Mail className="mt-0.5 size-4 shrink-0 text-[var(--amber-warm)]" aria-hidden />
              <a href={`mailto:${BUSINESS.email}`} className="link-underline">
                {BUSINESS.email}
              </a>
            </li>
            <li className="flex gap-3">
              <Phone className="mt-0.5 size-4 shrink-0 text-[var(--amber-warm)]" aria-hidden />
              <a href={BUSINESS.phoneHref} className="link-underline">
                {BUSINESS.phone}
              </a>
            </li>
            <li className="pl-7 text-xs uppercase tracking-[0.2em]">
              By appointment — including after hours
            </li>
          </ul>
        </div>
      </div>

      <div className="shell mt-14 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-6 text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        <span>
          © {new Date().getFullYear()} {BUSINESS.name}
        </span>
        <span>Certified Professional Framer · PPFA Master's Certification</span>
      </div>
    </footer>
  );
}

export function CallBar() {
  return (
    <a
      href={BUSINESS.phoneHref}
      className="btn-primary fixed bottom-5 left-1/2 z-40 -translate-x-1/2 md:left-auto md:right-8 md:bottom-8 md:translate-x-0"
    >
      <Phone className="size-3.5" aria-hidden />
      Call Shirley for an appointment
    </a>
  );
}
